import { Injectable, inject, DestroyRef, DOCUMENT } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { NotificationService } from './notification.service';
import { filter, fromEvent } from 'rxjs';
import { ErrorLoggerService } from './error/error-logger.service';

// How often (ms) to actively ask the SW to check for a new version.
// 30 min is a sensible balance — frequent enough that vets get fixes the same
// shift, infrequent enough that we don't hammer the SW or origin needlessly.
const POLL_INTERVAL_MS = 30 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly sw = inject(SwUpdate);
  private readonly notify = inject(NotificationService);
  private readonly errorLogger = inject(ErrorLoggerService);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private prompted = false;

  install(): void {
    if (!this.sw.isEnabled) return;

    this.sw.versionUpdates
      .pipe(
        filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.promptToReload());

    // Foreground / visibility comeback — common signal that the user has
    // returned to the PWA (especially on iOS where backgrounded apps suspend).
    fromEvent(this.document, 'visibilitychange')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.document.visibilityState === 'visible') this.checkForUpdate();
      });

    const win = this.document.defaultView;
    if (win) {
      // Window focus — catches alt-tab back to the browser window on desktop,
      // which visibilitychange misses (the page was never actually hidden).
      fromEvent(win, 'focus')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.checkForUpdate());

      // pageshow with persisted=true — the page was restored from the bfcache.
      // iOS PWAs lean on bfcache heavily and often skip visibilitychange on
      // restore, so this is the more reliable "came back" signal there.
      fromEvent<PageTransitionEvent>(win, 'pageshow')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (e.persisted) this.checkForUpdate();
        });
    }

    // Periodic poll — covers the long-session case where the user never
    // backgrounds the tab (desktop, mostly).
    const intervalId = setInterval(() => this.checkForUpdate(), POLL_INTERVAL_MS);
    this.destroyRef.onDestroy(() => clearInterval(intervalId));

    // Initial nudge — SW already runs its own startup check, but this makes
    // the first foreground-after-deploy reliably trigger an update prompt.
    void this.checkForUpdate();
  }

  private async checkForUpdate(): Promise<void> {
    if (!this.sw.isEnabled) return;
    try {
      await this.sw.checkForUpdate();
    } catch (error) {
      this.errorLogger.log(error, undefined, { context: 'AppUpdateService.checkForUpdate' });
    }
  }

  private promptToReload(): void {
    if (this.prompted) return;
    this.prompted = true;

    const ref = this.notify.open(
      'A new version of ViTails is available',
      'Reload',
      { duration: 0 },
    );

    ref.onAction().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async () => {
      try {
        await this.sw.activateUpdate();
      } catch (error) {
        this.errorLogger.log(error, undefined, { context: 'AppUpdateService.activateUpdate' });
      } finally {
        this.document.defaultView?.location.reload();
      }
    });
  }
}
